import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import OpcHeader from "../_shared/OpcHeader/OpcHeader";
import {
  OpcPageError,
  OpcPageLoading,
} from "../_shared/OpcPageState/OpcPageState";
import okrPlansCoachStore, {
  useOkrPlansCoachStore,
} from "../_store/okrPlansCoachStore";
import { companyPath } from "../_config/okrPlansCoachRoutes";
import CompanyCard from "./CompanyCard/CompanyCard";
import "../../_shared/ProjectStyles/ProjectDashboard.scss";

// ═══════════════════════════════════════════════════════════════════════════
// OKR Plans dashboard (coach) — today's Plans dashboard (BE 2.7; CD2, CD3,
// CD15): Overview, three badges from `totals`, one card per company.
// Order (CD31): companies with OKR plans first, locked ones (`has_okr_plans`
// false) after; inside each group the BE's order (most check-ins due first).
// ═══════════════════════════════════════════════════════════════════════════

const selectCompanies = (s) => s.companies;

// Stable: keeps the BE's order inside each group.
const plansFirst = (companies) => [
  ...companies.filter((c) => c.has_okr_plans),
  ...companies.filter((c) => !c.has_okr_plans),
];

const OkrPlansCoachDashboard = () => {
  const history = useHistory();
  const data = useOkrPlansCoachStore(selectCompanies);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    okrPlansCoachStore
      .getState()
      .fetchCompanies()
      .catch((err) => setError(err));
  }, []);
  useEffect(load, [load]);

  let body;
  if (error) body = <OpcPageError error={error} onRetry={load} />;
  else if (!data) body = <OpcPageLoading kind="dashboard" />;
  else {
    const companies = plansFirst(data.companies || []);
    const totals = data.totals || {};
    body = (
      <div className="p-3">
        <div className="Header d-flex flex-column">
          <span className="fw-bold dark-grey">Overview</span>
          <span className="light-text small">
            Select a company to view its team members and OKR plans
          </span>
        </div>
        <div className="px-3 py-4 d-flex align-items-center">
          <div className="details-badge px-3 d-flex align-items-center">
            <img src="/assets/images/manager.svg" alt="" />
            <span className="small light-text ms-2 me-1 mt-1">Companies</span>
            <span className="fw-bold ms-2 dark-grey mt-1">
              {totals.companies ?? companies.length}
            </span>
          </div>
          <div className="details-badge px-3 d-flex align-items-center mx-3">
            <img src="/assets/images/active-projects.svg" alt="" />
            <span className="small light-text ms-2 me-1 mt-1">Active OKRs</span>
            <span className="fw-bold ms-2 dark-grey mt-1">
              {totals.active_plans ?? 0}
            </span>
          </div>
          <div className="details-badge px-3 d-flex align-items-center">
            <img src="/assets/images/checkin.svg" alt="" />
            <span className="small light-text ms-2 me-1 mt-1">
              Total check-ins Due
            </span>
            <span className="fw-bold ms-2 dark-grey mt-1">
              {totals.checkins_due ?? 0}
            </span>
          </div>
        </div>
        {companies.length ? (
          <div className="px-3">
            <div className="row">
              {companies.map((c) => (
                <CompanyCard
                  key={c.company_id}
                  company={c}
                  onOpen={() =>
                    history.push(
                      companyPath(
                        c.company_id,
                        c.current_quarter?.quarter_company_id,
                      ),
                    )
                  }
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center opc-empty">
            <img
              src="/assets/images/agent-ligh-grey.svg"
              width={25}
              height={25}
              alt=""
            />
            <span className="fw-bold dark-grey small mt-4">
              No company is on AI Quarterly OKRs yet
            </span>
            <span className="light-text small mt-1">
              OKR plans appear here once a company&apos;s Quarterly OKRs are set
              and locked with the AI coach.
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="opc-screen">
      <OpcHeader crumbs={[{ title: "Dashboard" }]} />
      <div id="project-dashboard">{body}</div>
    </div>
  );
};

export default OkrPlansCoachDashboard;
